import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '@/index.css';

// ==================== API CONFIG ====================
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== AUTH CONTEXT ====================
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, password, referralCode = null) => {
    const payload = { name, email, password };
    if (referralCode) payload.referral_code = referralCode;
    const res = await axios.post(`${API}/auth/register`, payload);
    localStorage.setItem('token', res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== ICONS (Lucide-style) ====================
const Icons = {
  Menu: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>
  ),
  X: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
  ),
  ArrowUp: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 7-7 7 7"/><path d="M12 19V5"/></svg>
  ),
  ArrowDown: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="m19 12-7 7-7-7"/></svg>
  ),
  RotateCw: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></svg>
  ),
  Zap: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/></svg>
  ),
  Flame: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>
  ),
  Dumbbell: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6.5 6.5 11 11"/><path d="m21 21-1-1"/><path d="m3 3 1 1"/><path d="m18 22 4-4"/><path d="m2 6 4-4"/><path d="m3 10 7-7"/><path d="m14 21 7-7"/></svg>
  ),
  Library: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m16 6 4 14"/><path d="M12 6v14"/><path d="M8 8v12"/><path d="M4 4v16"/></svg>
  ),
  User: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  ),
  Check: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
  ),
  ChevronRight: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
  ),
  Clock: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
  ),
  Target: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
  ),
  Shield: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>
  ),
  Settings: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
  ),
  LogOut: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>
  ),
  Plus: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
  ),
  Trash: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
  ),
  Mail: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
  ),
  Phone: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
  ),
  MapPin: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>
  ),
};

// Pattern Icons mapping
const patternIcons = {
  'push': Icons.ArrowUp,
  'pull': Icons.ArrowDown,
  'squat': Icons.ArrowDown,
  'hinge': Icons.RotateCw,
  'rotation': Icons.RotateCw,
  'athletic': Icons.Zap,
  'ballistic': Icons.Flame,
  'stability': Icons.Shield,
};

// ==================== BRAND LOGO COMPONENTS ====================

// Circular Badge Logo - with transparent background handling and hover animation
const BadgeLogo = ({ size = 60, className = '' }) => (
  <div 
    className={`logo-hover-effect relative ${className}`}
    style={{ width: size, height: size }}
  >
    <img 
      src="https://customer-assets.emergentagent.com/job_a2502930-3d84-478a-90cd-13560982946b/artifacts/gstc47se_file_1772340471929.jpeg"
      alt="Move Free Live Free"
      className="object-contain w-full h-full rounded-full"
      style={{ 
        mixBlendMode: 'multiply',
        filter: 'contrast(1.1)',
      }}
    />
  </div>
);

// Gradient Text Logo with hover animation
const TextLogo = ({ className = '' }) => (
  <div className={`font-syne font-extrabold tracking-tighter leading-none logo-hover-effect ${className}`}>
    <span className="gradient-text">MOVE FREE</span>
    <br />
    <span className="gradient-text">LIVE FREE</span>
  </div>
);

// ==================== NAVBAR COMPONENT ====================
const Navbar = () => {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const navLinks = [
    { label: 'HOME', path: '/' },
    { label: 'WORKOUTS', path: '/workouts' },
    { label: 'QUICK HITTERS', path: '/get-after-it' },
    { label: 'EXERCISES', path: '/exercises' },
    { label: 'ARTICLES', path: '/articles' },
    { label: 'ASSESSMENT', path: '/assessment' },
    { label: 'CHALLENGES', path: '/challenges' },
    { label: 'MEMBERSHIP', path: '/membership' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="glass rounded-full px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 logo-hover-effect">
            <div 
              className="rounded-full overflow-hidden"
              style={{ 
                width: 40, 
                height: 40,
                border: '2px solid #00B4D8',
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_a2502930-3d84-478a-90cd-13560982946b/artifacts/gstc47se_file_1772340471929.jpeg"
                alt="Move Free Live Free"
                className="w-full h-full object-cover"
                style={{ transform: 'scale(1.25)' }}
              />
            </div>
            <span className="hidden sm:block font-syne font-bold text-white text-sm">MOVE FREE LIVE FREE</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map(link => (
              <Link 
                key={link.path} 
                to={link.path}
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                {user.is_admin && (
                  <Link to="/admin" className="text-sm font-medium text-cyan-400 hover:text-cyan-300" data-testid="nav-admin">
                    ADMIN
                  </Link>
                )}
                <button 
                  onClick={() => { logout(); navigate('/'); }}
                  className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
                  data-testid="logout-btn"
                >
                  LOGOUT
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium text-slate-300 hover:text-white transition-colors" data-testid="login-btn">
                  LOGIN
                </Link>
                <Link to="/register" className="btn-cyan text-sm px-6 py-2" data-testid="join-now-btn">
                  JOIN NOW
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <Icons.X /> : <Icons.Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden glass mt-2 rounded-2xl p-4 fade-in">
            <div className="flex flex-col gap-3">
              {navLinks.map(link => (
                <Link 
                  key={link.path} 
                  to={link.path}
                  className="text-sm font-medium text-slate-300 hover:text-white transition-colors py-2"
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
              <hr className="border-white/10 my-2" />
              {user ? (
                <>
                  {user.is_admin && (
                    <Link to="/admin" className="text-sm font-medium text-cyan-400" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-admin">
                      ADMIN
                    </Link>
                  )}
                  <button 
                    onClick={() => { logout(); navigate('/'); setMobileOpen(false); }}
                    className="text-sm font-medium text-slate-300 hover:text-white text-left py-2"
                    data-testid="mobile-logout-btn"
                  >
                    LOGOUT
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-medium text-slate-300 py-2" onClick={() => setMobileOpen(false)} data-testid="mobile-login-btn">
                    LOGIN
                  </Link>
                  <Link to="/register" className="btn-cyan text-sm text-center py-3" onClick={() => setMobileOpen(false)} data-testid="mobile-join-now-btn">
                    JOIN NOW
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// ==================== FOOTER COMPONENT ====================
const Footer = () => (
  <footer className="bg-ocean-deep border-t border-white/5 py-16 px-6">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        {/* Brand */}
        <div className="col-span-1 md:col-span-2">
          <div className="flex items-center gap-4 mb-6">
            <div 
              className="rounded-full overflow-hidden logo-hover-effect"
              style={{ 
                width: 50, 
                height: 50,
                border: '2px solid #00B4D8',
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_a2502930-3d84-478a-90cd-13560982946b/artifacts/gstc47se_file_1772340471929.jpeg"
                alt="Move Free Live Free"
                className="w-full h-full object-cover"
                style={{ transform: 'scale(1.25)' }}
              />
            </div>
            <TextLogo className="text-2xl" />
          </div>
          <p className="text-slate-400 max-w-md leading-relaxed">
            Movement Vitality & Functionality. Transform your body through the Prime8 Movement Patterns.
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-syne font-bold text-white mb-4">QUICK LINKS</h4>
          <ul className="space-y-3">
            <li><Link to="/workouts" className="text-slate-400 hover:text-cyan-400 transition-colors">Workouts</Link></li>
            <li><Link to="/exercises" className="text-slate-400 hover:text-cyan-400 transition-colors">Exercise Library</Link></li>
            <li><Link to="/membership" className="text-slate-400 hover:text-cyan-400 transition-colors">Membership</Link></li>
            <li><Link to="/about" className="text-slate-400 hover:text-cyan-400 transition-colors">About</Link></li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-syne font-bold text-white mb-4">CONTACT</h4>
          <ul className="space-y-3">
            <li className="flex items-center gap-2 text-slate-400">
              <Icons.Mail />
              <span>drjccarr@gmail.com</span>
            </li>
            <li><Link to="/contact" className="text-slate-400 hover:text-cyan-400 transition-colors">Contact Us</Link></li>
          </ul>
        </div>
      </div>

      <hr className="border-white/10 my-8" />
      
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-slate-500 text-sm">© 2024 Move Free Live Free. All rights reserved.</p>
        <p className="text-slate-600 text-sm italic">"Be water, my friend." - Bruce Lee</p>
      </div>
    </div>
  </footer>
);

// ==================== LANDING PAGE ====================
const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patterns, setPatterns] = useState([]);

  useEffect(() => {
    fetchPatterns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPatterns = async () => {
    try {
      const res = await axios.get(`${API}/patterns`);
      setPatterns(res.data);
    } catch (e) {
      console.error('Error fetching patterns:', e);
    }
  };

  return (
    <div className="min-h-screen bg-ocean-dark">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1657315312884-91cb6242c5be?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-ocean-dark/80 via-ocean-dark/60 to-ocean-dark"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-6 pt-20 max-w-5xl mx-auto">
          <div className="flex justify-center mb-8 wave-animation">
            <div 
              className="rounded-full overflow-hidden logo-hover-effect relative"
              style={{ 
                width: 130, 
                height: 130,
                border: '3px solid #00B4D8',
                boxShadow: '0 0 30px rgba(0, 180, 216, 0.4)',
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_a2502930-3d84-478a-90cd-13560982946b/artifacts/gstc47se_file_1772340471929.jpeg"
                alt="Move Free Live Free"
                className="w-full h-full object-cover"
                style={{ 
                  transform: 'scale(1.25)',
                }}
              />
            </div>
          </div>
          
          <h1 className="font-syne font-extrabold text-5xl md:text-7xl lg:text-8xl tracking-tighter mb-4 slide-up">
            <span className="gradient-text-horizontal">MOVE FREE</span>
          </h1>
          <h1 className="font-syne font-extrabold text-5xl md:text-7xl lg:text-8xl tracking-tighter mb-8 slide-up" style={{ animationDelay: '0.1s' }}>
            <span className="gradient-text-horizontal">LIVE FREE</span>
          </h1>

          <p className="text-xl md:text-2xl text-slate-300 font-light mb-4 slide-up" style={{ animationDelay: '0.2s' }}>
            Movement Vitality & Functionality
          </p>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-12 slide-up" style={{ animationDelay: '0.3s' }}>
            Access personalized programs, expert guidance, and a community dedicated to unlocking your body's potential.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center slide-up" style={{ animationDelay: '0.4s' }}>
            <button 
              onClick={() => navigate(user ? '/workouts' : '/register')}
              className="btn-primary text-lg"
              data-testid="start-journey-btn"
            >
              START YOUR JOURNEY
            </button>
            <button 
              onClick={() => navigate('/exercises')}
              className="btn-secondary text-lg"
              data-testid="explore-movements-btn"
            >
              EXPLORE MOVEMENTS
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <Icons.ArrowDown />
        </div>
      </section>

      {/* Prime8 Movement Patterns Section */}
      <section className="py-24 px-6 bg-ocean-dark" data-testid="patterns-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-syne font-bold text-4xl md:text-5xl text-white mb-4">
              THE <span className="gradient-text-horizontal">PRIME8</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Master these eight fundamental movement patterns to build a body that moves freely and powerfully.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger-children">
            {patterns.map((pattern, index) => {
              const IconComponent = patternIcons[pattern.id] || Icons.Target;
              return (
                <div key={pattern.id} className="pattern-card" style={{ opacity: 0 }}>
                  <div className="w-12 h-12 rounded-xl bg-cyan-primary/10 flex items-center justify-center text-cyan-primary mb-4">
                    <IconComponent />
                  </div>
                  <h3 className="font-syne font-bold text-xl text-white mb-2">{pattern.name}</h3>
                  <p className="text-slate-400 text-sm mb-4">{pattern.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {pattern.examples?.slice(0, 3).map((ex, i) => (
                      <span key={i} className="text-xs bg-white/5 text-slate-300 px-2 py-1 rounded-full">
                        {ex}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-ocean-dark to-ocean-deep" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-syne font-bold text-4xl md:text-5xl text-white mb-4">
              JOIN THE MOVEMENT
            </h2>
            <p className="text-slate-400 text-lg">
              Start your 7-day free trial today
            </p>
          </div>

          <div className="glass-card p-8 md:p-12 text-center glow-cyan">
            <div className="inline-block bg-cyan-primary/10 text-cyan-400 text-sm font-semibold px-4 py-1 rounded-full mb-6">
              7-DAY FREE TRIAL
            </div>
            
            <div className="mb-8">
              <span className="text-6xl font-syne font-bold text-white">$25</span>
              <span className="text-slate-400 text-xl">/month</span>
            </div>

            <ul className="space-y-4 mb-10 text-left max-w-sm mx-auto">
              {[
                'Access to all weekly workouts',
                'Prime8 Movement patterns training',
                'Complete exercise library',
                'Progress tracking',
                '15% discount on gear',
                'Cancel anytime',
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-300">
                  <div className="w-5 h-5 rounded-full bg-cyan-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icons.Check />
                  </div>
                  {feature}
                </li>
              ))}
            </ul>

            <button 
              onClick={() => navigate(user ? '/membership' : '/register')}
              className="btn-primary text-lg w-full max-w-sm"
              data-testid="start-free-trial-btn"
            >
              START FREE TRIAL
            </button>
            
            <p className="text-slate-500 text-sm mt-4">No credit card required for trial</p>
          </div>
        </div>
      </section>

      {/* Philosophy Quote */}
      <section className="py-24 px-6 bg-ocean-deep">
        <div className="max-w-4xl mx-auto text-center">
          <blockquote className="font-syne text-3xl md:text-4xl text-white italic leading-relaxed mb-6">
            "Empty your mind, be formless, shapeless — like water."
          </blockquote>
          <cite className="text-cyan-400 text-lg">— Bruce Lee</cite>
        </div>
      </section>
    </div>
  );
};

// ==================== AUTH PAGES ====================
const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState('email'); // 'email' or 'username'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (loginMode === 'username') {
        // Username login for family accounts
        const res = await axios.post(`${API}/auth/login-username`, { username: email, password });
        localStorage.setItem('token', res.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
        navigate(res.data.user.is_admin ? '/admin' : '/workouts');
        window.location.reload(); // Refresh to update auth state
      } else {
        const user = await login(email, password);
        navigate(user.is_admin ? '/admin' : '/workouts');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ocean-dark flex items-center justify-center px-6 pt-20">
      <div className="glass-card p-8 w-full max-w-md" data-testid="login-form">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div 
              className="rounded-full overflow-hidden logo-hover-effect"
              style={{ 
                width: 70, 
                height: 70,
                border: '2px solid #00B4D8',
                boxShadow: '0 0 20px rgba(0, 180, 216, 0.3)',
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_a2502930-3d84-478a-90cd-13560982946b/artifacts/gstc47se_file_1772340471929.jpeg"
                alt="Move Free Live Free"
                className="w-full h-full object-cover"
                style={{ transform: 'scale(1.25)' }}
              />
            </div>
          </div>
          <h1 className="font-syne font-bold text-3xl text-white">Welcome Back</h1>
          <p className="text-slate-400 mt-2">Sign in to continue your journey</p>
        </div>

        {/* Login Mode Toggle */}
        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={() => { setLoginMode('email'); setEmail(''); }}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
              loginMode === 'email' 
                ? 'bg-cyan-primary text-ocean-dark' 
                : 'bg-ocean-surface text-slate-400 hover:text-white'
            }`}
          >
            Email Login
          </button>
          <button
            type="button"
            data-testid="username-login-toggle"
            onClick={() => { setLoginMode('username'); setEmail(''); }}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
              loginMode === 'username' 
                ? 'bg-cyan-primary text-ocean-dark' 
                : 'bg-ocean-surface text-slate-400 hover:text-white'
            }`}
          >
            Username Login
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {loginMode === 'email' ? 'Email' : 'Username'}
            </label>
            <input
              type={loginMode === 'email' ? 'email' : 'text'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
              placeholder={loginMode === 'email' ? 'you@example.com' : 'username'}
              required
              data-testid="login-email"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-slate-300">Password</label>
              {loginMode === 'email' && (
                <Link to="/forgot-password" className="text-sm text-cyan-400 hover:text-cyan-300" data-testid="forgot-password-link">
                  Forgot Password?
                </Link>
              )}
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
              placeholder="••••••••"
              required
              data-testid="login-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-cyan w-full disabled:opacity-50"
            data-testid="login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-slate-400 mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-cyan-400 hover:text-cyan-300">Sign up</Link>
        </p>
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(name, email, password, referralCode);
      navigate('/workouts');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ocean-dark flex items-center justify-center px-6 pt-20">
      <div className="glass-card p-8 w-full max-w-md" data-testid="register-form">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div 
              className="rounded-full overflow-hidden logo-hover-effect"
              style={{ 
                width: 70, 
                height: 70,
                border: '2px solid #00B4D8',
                boxShadow: '0 0 20px rgba(0, 180, 216, 0.3)',
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_a2502930-3d84-478a-90cd-13560982946b/artifacts/gstc47se_file_1772340471929.jpeg"
                alt="Move Free Live Free"
                className="w-full h-full object-cover"
                style={{ transform: 'scale(1.25)' }}
              />
            </div>
          </div>
          <h1 className="font-syne font-bold text-3xl text-white">Start Your Journey</h1>
          <p className="text-slate-400 mt-2">Create your account to begin</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
              placeholder="John Doe"
              required
              data-testid="register-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
              placeholder="you@example.com"
              required
              data-testid="register-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
              placeholder="••••••••"
              required
              minLength={6}
              data-testid="register-password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Referral Code (Optional)</label>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50 uppercase"
              placeholder="Enter referral code"
              maxLength={6}
              data-testid="register-referral"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-cyan w-full disabled:opacity-50"
            data-testid="register-submit"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-slate-400 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-cyan-400 hover:text-cyan-300">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

// ==================== FORGOT PASSWORD PAGE ====================
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email,
        origin_url: window.location.origin
      });
      setSent(true);
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ocean-dark flex items-center justify-center px-6 pt-20">
      <div className="glass-card p-8 w-full max-w-md" data-testid="forgot-password-form">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div 
              className="rounded-full overflow-hidden logo-hover-effect"
              style={{ 
                width: 70, 
                height: 70,
                border: '2px solid #00B4D8',
                boxShadow: '0 0 20px rgba(0, 180, 216, 0.3)',
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_a2502930-3d84-478a-90cd-13560982946b/artifacts/gstc47se_file_1772340471929.jpeg"
                alt="Move Free Live Free"
                className="w-full h-full object-cover"
                style={{ transform: 'scale(1.25)' }}
              />
            </div>
          </div>
          <h1 className="font-syne font-bold text-3xl text-white">Reset Password</h1>
          <p className="text-slate-400 mt-2">Enter your email to receive a reset link</p>
        </div>

        {sent ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Icons.Check />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Check Your Email</h2>
            <p className="text-slate-400 mb-6">
              If an account exists with <span className="text-cyan-400">{email}</span>, you'll receive a password reset link shortly.
            </p>
            <Link to="/login" className="text-cyan-400 hover:text-cyan-300">
              Return to Login
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-email"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-cyan w-full disabled:opacity-50"
                data-testid="forgot-submit"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            <p className="text-center text-slate-400 mt-6">
              Remember your password?{' '}
              <Link to="/login" className="text-cyan-400 hover:text-cyan-300">Sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== RESET PASSWORD PAGE ====================
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setError('Invalid reset link');
      return;
    }
    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
      setValid(res.data.valid);
      setEmail(res.data.email);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired reset link');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-ocean-dark flex items-center justify-center pt-20">
        <div className="text-cyan-400 animate-pulse">Verifying reset link...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ocean-dark flex items-center justify-center px-6 pt-20">
      <div className="glass-card p-8 w-full max-w-md" data-testid="reset-password-form">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div 
              className="rounded-full overflow-hidden logo-hover-effect"
              style={{ 
                width: 70, 
                height: 70,
                border: '2px solid #00B4D8',
                boxShadow: '0 0 20px rgba(0, 180, 216, 0.3)',
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_a2502930-3d84-478a-90cd-13560982946b/artifacts/gstc47se_file_1772340471929.jpeg"
                alt="Move Free Live Free"
                className="w-full h-full object-cover"
                style={{ transform: 'scale(1.25)' }}
              />
            </div>
          </div>
          <h1 className="font-syne font-bold text-3xl text-white">
            {success ? 'Password Reset!' : 'Create New Password'}
          </h1>
          {!success && valid && (
            <p className="text-slate-400 mt-2">
              For <span className="text-cyan-400">{email}</span>
            </p>
          )}
        </div>

        {success ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Icons.Check />
            </div>
            <p className="text-slate-300 mb-4">
              Your password has been reset successfully.
            </p>
            <p className="text-slate-400 text-sm">Redirecting to login...</p>
          </div>
        ) : !valid ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <Icons.X />
            </div>
            <p className="text-red-400 mb-4">{error}</p>
            <Link to="/forgot-password" className="text-cyan-400 hover:text-cyan-300">
              Request a new reset link
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">New Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="new-password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="confirm-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-cyan w-full disabled:opacity-50"
                data-testid="reset-submit"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== WORKOUTS PAGE ====================
const WorkoutsPage = () => {
  const [allWorkouts, setAllWorkouts] = useState([]);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [activeDay, setActiveDay] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('current'); // 'current' or 'calendar'

  useEffect(() => {
    fetchWorkouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWorkouts = async () => {
    try {
      const res = await axios.get(`${API}/workouts/all`);
      setAllWorkouts(res.data);
      // Set the current (active) workout as default
      const current = res.data.find(w => w.is_active) || res.data[0];
      setSelectedWorkout(current);
    } catch (e) {
      console.error('Error fetching workouts:', e);
    } finally {
      setLoading(false);
    }
  };

  const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
  const currentDay = selectedWorkout?.days?.find(d => d.day === activeDay);

  // Group workouts by year for calendar
  const workoutsByYear = allWorkouts.reduce((acc, w) => {
    const year = w.year || 2026;
    if (!acc[year]) acc[year] = [];
    acc[year].push(w);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-ocean-dark flex items-center justify-center pt-20">
        <div className="text-cyan-400 animate-pulse">Loading workouts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ocean-dark pt-24 pb-16 px-6" data-testid="workouts-page">
      <div className="max-w-6xl mx-auto">
        {/* View Toggle */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setViewMode('current')}
            data-testid="view-current-week"
            className={`px-6 py-2 rounded-full font-medium transition-all ${
              viewMode === 'current' 
                ? 'bg-cyan-primary text-ocean-dark' 
                : 'bg-ocean-surface text-slate-300 hover:text-white'
            }`}
          >
            Current Week
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            data-testid="view-past-weeks"
            className={`px-6 py-2 rounded-full font-medium transition-all ${
              viewMode === 'calendar' 
                ? 'bg-cyan-primary text-ocean-dark' 
                : 'bg-ocean-surface text-slate-300 hover:text-white'
            }`}
          >
            Past Weeks
          </button>
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <div className="mb-12">
            <h2 className="font-syne font-bold text-2xl text-white mb-6 text-center">Workout Archive</h2>
            <p className="text-slate-400 text-center mb-8">Browse past workouts to revisit or do again</p>
            
            {Object.entries(workoutsByYear).sort(([a], [b]) => b - a).map(([year, workouts]) => (
              <div key={year} className="mb-8">
                <h3 className="text-cyan-400 font-medium mb-4">{year}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {workouts.map(w => (
                    <button
                      key={w.id}
                      onClick={() => { setSelectedWorkout(w); setViewMode('current'); setActiveDay(1); }}
                      data-testid={`archive-workout-${w.id}`}
                      className={`glass-card p-4 text-left hover:scale-102 transition-all ${
                        selectedWorkout?.id === w.id ? 'ring-2 ring-cyan-primary' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-cyan-400 text-sm font-medium">Week {w.week_number}</span>
                        {w.is_active && (
                          <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded">Current</span>
                        )}
                      </div>
                      <h4 className="text-white font-medium text-sm truncate">{w.title}</h4>
                      <p className="text-slate-500 text-xs mt-1">{w.days?.length || 0} days</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            
            {allWorkouts.length === 0 && (
              <p className="text-center text-slate-400">No past workouts yet</p>
            )}
          </div>
        )}

        {/* Current Workout View */}
        {viewMode === 'current' && selectedWorkout && (
          <>
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-2 mb-2">
                {!selectedWorkout.is_active && (
                  <span className="bg-slate-500/20 text-slate-400 text-xs px-3 py-1 rounded-full">
                    Past Week
                  </span>
                )}
                <span className="text-cyan-400 text-sm">Week {selectedWorkout.week_number}</span>
              </div>
              <h1 className="font-syne font-bold text-4xl md:text-5xl text-white mb-4">
                {selectedWorkout.title}
              </h1>
              <p className="text-slate-400 text-lg">{selectedWorkout.description}</p>
            </div>

            {/* Day Tabs */}
            <div className="flex justify-center gap-2 md:gap-4 mb-12 overflow-x-auto pb-4">
              {dayNames.map((name, index) => (
                <button
                  key={index}
                  onClick={() => setActiveDay(index + 1)}
                  className={`day-tab whitespace-nowrap ${activeDay === index + 1 ? 'active' : ''}`}
                  data-testid={`day-tab-${index + 1}`}
                >
                  {name}
                </button>
              ))}
            </div>

            {/* Workout Content */}
            {currentDay ? (
              <div className="fade-in">
                {/* Workout Header Card */}
                <div className="glass-card p-8 mb-8">
                  <div className="flex flex-col md:flex-row gap-8">
                    {currentDay.card_image_url && (
                      <div className="md:w-1/3">
                        <img 
                          src={currentDay.card_image_url} 
                          alt={currentDay.title}
                          className="w-full h-48 md:h-full object-cover rounded-2xl"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <h2 className="font-syne font-bold text-3xl text-white mb-2">{currentDay.title}</h2>
                      <p className="text-cyan-400 text-lg mb-4">{currentDay.subtitle}</p>
                      
                      <div className="flex flex-wrap gap-4 mb-6">
                        <div className="flex items-center gap-2 text-slate-300">
                          <Icons.Clock />
                          <span>{currentDay.duration}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-300">
                          <Icons.Target />
                          <span>{currentDay.level}</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {currentDay.focus_areas?.map((area, i) => (
                          <span key={i} className="bg-cyan-primary/10 text-cyan-400 px-3 py-1 rounded-full text-sm">
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Warmup */}
                {currentDay.warmup?.length > 0 && (
                  <div className="glass-card p-6 mb-6">
                    <h3 className="font-syne font-bold text-xl text-white mb-4">Warmup</h3>
                    <ul className="space-y-2">
                      {currentDay.warmup.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-300">
                          <span className="w-6 h-6 rounded-full bg-cyan-primary/20 flex items-center justify-center text-cyan-400 text-sm flex-shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Exercises */}
                <div className="glass-card p-6 mb-6">
                  <h3 className="font-syne font-bold text-xl text-white mb-6">Exercises</h3>
                  <div className="space-y-4">
                    {currentDay.exercises?.map((ex, i) => (
                      <div key={i} className="flex items-start justify-between bg-ocean-surface/50 rounded-xl p-4 hover:bg-ocean-surface transition-colors">
                        <div className="flex items-start gap-4">
                          <span className="w-8 h-8 rounded-lg bg-cyan-primary/20 flex items-center justify-center text-cyan-400 font-bold flex-shrink-0">
                            {i + 1}
                          </span>
                          <div>
                            <h4 className="text-white font-medium">{ex.name}</h4>
                            {ex.notes && <p className="text-slate-400 text-sm mt-1">{ex.notes}</p>}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <span className="text-cyan-400 font-semibold">{ex.sets} × {ex.reps}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Burnout */}
                {currentDay.burnout?.length > 0 && (
                  <div className="glass-card p-6 border-l-4 border-l-red-500">
                    <h3 className="font-syne font-bold text-xl text-white mb-4 flex items-center gap-2">
                      <Icons.Flame />
                      Burnout
                    </h3>
                    <ul className="space-y-2">
                      {currentDay.burnout.map((item, i) => (
                        <li key={i} className="text-slate-300">{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-slate-400">No workout scheduled for this day</p>
              </div>
            )}
          </>
        )}

        {viewMode === 'current' && !selectedWorkout && (
          <div className="text-center py-16">
            <p className="text-slate-400 text-lg mb-4">No workouts available yet</p>
            <p className="text-slate-500">Check back soon - new workouts are posted weekly!</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== EXERCISES PAGE ====================
const ExercisesPage = () => {
  const [exercises, setExercises] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [selectedPattern, setSelectedPattern] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPattern]);

  const fetchData = async () => {
    try {
      const [exercisesRes, patternsRes] = await Promise.all([
        axios.get(`${API}/exercises${selectedPattern ? `?pattern=${encodeURIComponent(selectedPattern)}` : ''}`),
        axios.get(`${API}/patterns`)
      ]);
      setExercises(exercisesRes.data);
      setPatterns(patternsRes.data);
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ocean-dark pt-24 pb-16 px-6" data-testid="exercises-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-syne font-bold text-4xl md:text-5xl text-white mb-4">
            Exercise Library
          </h1>
          <p className="text-slate-400 text-lg">
            Master the movements that matter
          </p>
        </div>

        {/* Pattern Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <button
            onClick={() => setSelectedPattern('')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              !selectedPattern 
                ? 'bg-cyan-primary text-ocean-dark' 
                : 'bg-ocean-surface text-slate-300 hover:bg-ocean-surface/80'
            }`}
            data-testid="filter-all"
          >
            All Patterns
          </button>
          {patterns.map(pattern => (
            <button
              key={pattern.id}
              onClick={() => setSelectedPattern(pattern.name)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedPattern === pattern.name 
                  ? 'bg-cyan-primary text-ocean-dark' 
                  : 'bg-ocean-surface text-slate-300 hover:bg-ocean-surface/80'
              }`}
              data-testid={`filter-${pattern.id}`}
            >
              {pattern.name}
            </button>
          ))}
        </div>

        {/* Exercises Grid */}
        {loading ? (
          <div className="text-center text-cyan-400 animate-pulse">Loading exercises...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {exercises.map(exercise => (
              <div key={exercise.id} className="glass-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-syne font-bold text-xl text-white">{exercise.name}</h3>
                    <span className="text-cyan-400 text-sm">{exercise.pattern}</span>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-cyan-primary/10 flex items-center justify-center text-cyan-400">
                    <Icons.Dumbbell />
                  </div>
                </div>
                <p className="text-slate-400 text-sm mb-4">{exercise.description}</p>
                {exercise.coaching_cues?.length > 0 && (
                  <div>
                    <h4 className="text-slate-300 text-sm font-medium mb-2">Coaching Cues:</h4>
                    <ul className="space-y-1">
                      {exercise.coaching_cues.map((cue, i) => (
                        <li key={i} className="text-slate-500 text-sm flex items-start gap-2">
                          <span className="text-cyan-400 mt-1">•</span>
                          {cue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ARTICLES PAGE ====================
const ArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedArticle, setExpandedArticle] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/articles`);
      setArticles(res.data);
    } catch (e) {
      console.error('Error fetching articles:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const toggleArticle = (slug) => {
    setExpandedArticle(expandedArticle === slug ? null : slug);
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16 px-6" data-testid="articles-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="font-syne font-bold text-5xl md:text-6xl text-white mb-4 tracking-tight">
            ARTICLES
          </h1>
          <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mb-4"></div>
          <p className="text-amber-400/80 text-lg tracking-wide">
            For Those Who Bring The Vibes.
          </p>
        </div>

        {/* Articles */}
        {loading ? (
          <div className="text-center text-amber-400 animate-pulse">Loading articles...</div>
        ) : (
          <div className="space-y-8">
            {articles.map((article) => (
              <article 
                key={article.slug} 
                className="border border-amber-500/20 rounded-lg overflow-hidden bg-gradient-to-b from-zinc-900/50 to-black hover:border-amber-500/40 transition-all duration-300"
                data-testid={`article-${article.slug}`}
              >
                {/* Card Header */}
                <div className="p-8">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-medium rounded-full tracking-wider uppercase">
                      {article.category}
                    </span>
                    <span className="text-zinc-500 text-sm">
                      {formatDate(article.date)}
                    </span>
                  </div>
                  
                  <h2 className="font-syne font-bold text-2xl md:text-3xl text-white mb-4 leading-tight">
                    {article.title}
                  </h2>
                  
                  <p className="text-zinc-400 text-lg leading-relaxed mb-6">
                    {article.excerpt}
                  </p>
                  
                  <button
                    onClick={() => toggleArticle(article.slug)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 hover:border-amber-500/50 text-amber-400 rounded-lg transition-all duration-300 font-medium"
                    data-testid={`read-more-${article.slug}`}
                  >
                    {expandedArticle === article.slug ? (
                      <>
                        <span>Show Less</span>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </>
                    ) : (
                      <>
                        <span>Read More</span>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>

                {/* Expanded Content */}
                {expandedArticle === article.slug && (
                  <div className="border-t border-amber-500/20 bg-zinc-900/30">
                    <div className="p-8">
                      <div className="w-12 h-0.5 bg-amber-500/50 mb-8"></div>
                      <div className="prose prose-invert prose-amber max-w-none">
                        {article.content.split('\n').map((paragraph, idx) => {
                          if (!paragraph.trim()) return <br key={idx} />;
                          if (paragraph.startsWith('- ')) {
                            return (
                              <div key={idx} className="flex items-start gap-3 text-zinc-300 my-2">
                                <span className="text-amber-500 mt-1">•</span>
                                <span>{paragraph.substring(2)}</span>
                              </div>
                            );
                          }
                          return (
                            <p key={idx} className="text-zinc-300 leading-relaxed mb-4">
                              {paragraph}
                            </p>
                          );
                        })}
                      </div>
                      <div className="mt-8 pt-6 border-t border-amber-500/10">
                        <button
                          onClick={() => toggleArticle(article.slug)}
                          className="text-amber-400/60 hover:text-amber-400 text-sm flex items-center gap-2 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                          Close Article
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        {/* Footer decoration */}
        <div className="mt-16 text-center">
          <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-amber-500/50 to-transparent mx-auto"></div>
          <p className="text-zinc-600 text-sm mt-6">Move Free. Live Free.</p>
        </div>
      </div>
    </div>
  );
};

// ==================== MEMBERSHIP PAGE ====================
const MembershipPage = () => {
  const { user, loading: authLoading, fetchUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setMessage('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setMessage('Payment successful! Welcome to Move Free Live Free!');
        await fetchUser();
        setTimeout(() => navigate('/workouts'), 2000);
      } else if (res.data.status === 'expired') {
        setMessage('Payment session expired. Please try again.');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (e) {
      console.error('Error checking payment:', e);
      setMessage('Error verifying payment. Please contact support.');
    }
  };

  const handleCheckout = async () => {
    if (authLoading) return; // Wait for auth to load
    if (!user) {
      navigate('/register');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/create`, {
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (e) {
      console.error('Checkout error:', e);
      setMessage(e.response?.data?.detail || 'Error creating checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ocean-dark pt-24 pb-16 px-6" data-testid="membership-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-syne font-bold text-4xl md:text-5xl text-white mb-4">
            {user?.is_member ? 'Your Membership' : 'Join the Movement'}
          </h1>
          <p className="text-slate-400 text-lg">
            {user?.is_member 
              ? 'You have full access to all features' 
              : 'Unlock your full potential with a membership'}
          </p>
        </div>

        {message && (
          <div className={`text-center p-4 rounded-xl mb-8 ${
            message.includes('successful') 
              ? 'bg-green-500/10 border border-green-500/20 text-green-400' 
              : 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400'
          }`}>
            {message}
          </div>
        )}

        {user?.is_member ? (
          <div className="glass-card p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-cyan-primary/20 flex items-center justify-center mx-auto mb-6">
              <Icons.Shield />
            </div>
            <h2 className="font-syne font-bold text-2xl text-white mb-2">Active Member</h2>
            <p className="text-slate-400 mb-6">
              Status: <span className="text-cyan-400">{user.subscription_status}</span>
            </p>
            {user.trial_end && (
              <p className="text-slate-500 text-sm">
                Trial ends: {new Date(user.trial_end).toLocaleDateString()}
              </p>
            )}
          </div>
        ) : (
          <div className="glass-card p-8 md:p-12 text-center glow-cyan">
            <div className="inline-block bg-cyan-primary/10 text-cyan-400 text-sm font-semibold px-4 py-1 rounded-full mb-6">
              7-DAY FREE TRIAL
            </div>
            
            <div className="mb-8">
              <span className="text-6xl font-syne font-bold text-white">$25</span>
              <span className="text-slate-400 text-xl">/month</span>
            </div>

            <ul className="space-y-4 mb-10 text-left max-w-sm mx-auto">
              {[
                'Access to all weekly workouts',
                'Prime8 Movement patterns training',
                'Complete exercise library',
                'Progress tracking',
                '15% discount on gear',
                'Cancel anytime',
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-300">
                  <div className="w-5 h-5 rounded-full bg-cyan-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icons.Check />
                  </div>
                  {feature}
                </li>
              ))}
            </ul>

            <button 
              onClick={handleCheckout}
              disabled={loading}
              className="btn-primary text-lg w-full max-w-sm disabled:opacity-50"
              data-testid="checkout-btn"
            >
              {loading ? 'Processing...' : 'START FREE TRIAL'}
            </button>
            
            <p className="text-slate-500 text-sm mt-4">Secure checkout powered by Stripe</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== GET AFTER IT PAGE ====================
const GetAfterItPage = () => {
  const [workouts, setWorkouts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      const [workoutsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/get-after-it${selectedCategory ? `?category=${selectedCategory}` : ''}`),
        axios.get(`${API}/get-after-it/categories`)
      ]);
      setWorkouts(workoutsRes.data);
      setCategories(categoriesRes.data.categories);
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setLoading(false);
    }
  };

  const categoryColors = {
    'Hardcore': 'from-red-500 to-orange-500',
    'Pumpalooza': 'from-purple-500 to-pink-500',
    'Athlete Session': 'from-cyan-500 to-blue-500'
  };

  const workoutTypeIcons = {
    'MetCon': Icons.Flame,
    'AMRAP': Icons.RotateCw,
    'EMOM': Icons.Clock
  };

  return (
    <div className="min-h-screen bg-ocean-dark pt-24 pb-16 px-6" data-testid="get-after-it-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-cyan-400 text-sm font-medium tracking-wider mb-2">30 MIN QUICK HITTER WORKOUTS</p>
          <h1 className="font-syne font-bold text-4xl md:text-5xl text-white mb-4">
            GET AFTER IT!
          </h1>
          <p className="text-slate-400 text-lg">
            High-intensity MetCon, AMRAP, and EMOM workouts - 30 minutes or less
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button
            onClick={() => setSelectedCategory('')}
            className={`px-6 py-3 rounded-full text-sm font-bold transition-all ${
              !selectedCategory 
                ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' 
                : 'bg-ocean-surface text-slate-300 hover:bg-ocean-surface/80'
            }`}
            data-testid="filter-all"
          >
            ALL WORKOUTS
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.name)}
              className={`px-6 py-3 rounded-full text-sm font-bold transition-all ${
                selectedCategory === cat.name 
                  ? `bg-gradient-to-r ${categoryColors[cat.name] || 'from-cyan-500 to-blue-500'} text-white` 
                  : 'bg-ocean-surface text-slate-300 hover:bg-ocean-surface/80'
              }`}
              data-testid={`filter-${cat.id}`}
            >
              {cat.name.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Category Descriptions */}
        {!selectedCategory && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {categories.map(cat => (
              <div 
                key={cat.id} 
                className="glass-card p-6 cursor-pointer hover:scale-105 transition-transform"
                onClick={() => setSelectedCategory(cat.name)}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${categoryColors[cat.name] || 'from-cyan-500 to-blue-500'} flex items-center justify-center text-white mb-4`}>
                  <Icons.Flame />
                </div>
                <h3 className="font-syne font-bold text-xl text-white mb-2">{cat.name}</h3>
                <p className="text-slate-400 text-sm">{cat.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* Workouts Grid */}
        {loading ? (
          <div className="text-center text-cyan-400 animate-pulse">Loading workouts...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workouts.map(workout => {
              const TypeIcon = workoutTypeIcons[workout.workout_type] || Icons.Target;
              return (
                <div 
                  key={workout.id} 
                  className="glass-card p-6 cursor-pointer hover:scale-102 transition-all"
                  onClick={() => setSelectedWorkout(workout)}
                  data-testid={`workout-card-${workout.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${categoryColors[workout.category]} text-white`}>
                      {workout.category}
                    </div>
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <TypeIcon />
                      {workout.workout_type}
                    </div>
                  </div>
                  <h3 className="font-syne font-bold text-xl text-white mb-2">{workout.title}</h3>
                  <p className="text-slate-400 text-sm mb-4">{workout.description}</p>
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Icons.Clock />
                      {workout.duration}
                    </span>
                    {workout.rounds && (
                      <span>{workout.rounds} rounds</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Workout Detail Modal */}
        {selectedWorkout && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setSelectedWorkout(null)}>
            <div className="glass-card p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${categoryColors[selectedWorkout.category]} text-white mb-2`}>
                    {selectedWorkout.category} • {selectedWorkout.workout_type}
                  </div>
                  <h2 className="font-syne font-bold text-3xl text-white">{selectedWorkout.title}</h2>
                </div>
                <button onClick={() => setSelectedWorkout(null)} className="text-slate-400 hover:text-white">
                  <Icons.X />
                </button>
              </div>

              <p className="text-slate-300 mb-6">{selectedWorkout.description}</p>

              <div className="flex flex-wrap gap-4 mb-6">
                <div className="bg-ocean-surface rounded-xl px-4 py-2">
                  <span className="text-slate-500 text-sm">Duration</span>
                  <p className="text-white font-bold">{selectedWorkout.duration}</p>
                </div>
                {selectedWorkout.time_cap && (
                  <div className="bg-ocean-surface rounded-xl px-4 py-2">
                    <span className="text-slate-500 text-sm">Time Cap</span>
                    <p className="text-white font-bold">{selectedWorkout.time_cap}</p>
                  </div>
                )}
                {selectedWorkout.rounds && (
                  <div className="bg-ocean-surface rounded-xl px-4 py-2">
                    <span className="text-slate-500 text-sm">Rounds</span>
                    <p className="text-white font-bold">{selectedWorkout.rounds}</p>
                  </div>
                )}
              </div>

              {selectedWorkout.equipment?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-slate-400 text-sm font-medium mb-2">Equipment Needed</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedWorkout.equipment.map((eq, i) => (
                      <span key={i} className="bg-cyan-primary/10 text-cyan-400 px-3 py-1 rounded-full text-sm">
                        {eq}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h3 className="font-syne font-bold text-xl text-white mb-4">The Workout</h3>
                <div className="space-y-3">
                  {selectedWorkout.exercises?.map((ex, i) => (
                    <div key={i} className="flex items-center justify-between bg-ocean-surface/50 rounded-xl p-4">
                      <div className="flex items-center gap-4">
                        <span className="w-8 h-8 rounded-lg bg-cyan-primary/20 flex items-center justify-center text-cyan-400 font-bold">
                          {i + 1}
                        </span>
                        <div>
                          <h4 className="text-white font-medium">{ex.name}</h4>
                          {ex.notes && <p className="text-slate-400 text-sm">{ex.notes}</p>}
                        </div>
                      </div>
                      <div className="text-right">
                        {ex.reps && <span className="text-cyan-400 font-semibold">{ex.reps}</span>}
                        {ex.duration && <span className="text-cyan-400 font-semibold">{ex.duration}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedWorkout.notes && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
                  <h4 className="text-yellow-400 font-medium mb-2">Notes</h4>
                  <p className="text-slate-300 text-sm">{selectedWorkout.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== AI ASSISTANT COMPONENT ====================
const AIAssistant = ({ onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = React.useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: userMessage,
        session_id: sessionId
      });
      
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) {
      console.error('AI Chat error:', e);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const generateWorkout = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/generate-weekly-workout`, {
        week_number: 3,
        focus: "balanced Prime8 coverage with progressive intensity"
      });
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `✅ **Workout Draft Created!**\n\n**${res.data.workout.title}**\n${res.data.workout.description}\n\nThis workout will auto-publish on Sunday at 8pm if not approved earlier. Go to the Workout Drafts section to review and edit.`
      }]);
    } catch (e) {
      console.error('Generate workout error:', e);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Failed to generate workout. Please try again.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[600px] glass-card rounded-2xl flex flex-col z-50" data-testid="ai-assistant">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
            <Icons.Zap />
          </div>
          <div>
            <h3 className="font-syne font-bold text-white">AI Assistant</h3>
            <p className="text-xs text-slate-400">Powered by Move Free</p>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <Icons.X />
        </button>
      </div>

      {/* Quick Actions */}
      <div className="p-3 border-b border-white/10 flex gap-2">
        <button 
          onClick={generateWorkout}
          disabled={loading}
          className="text-xs bg-cyan-primary/20 text-cyan-400 px-3 py-1.5 rounded-full hover:bg-cyan-primary/30 disabled:opacity-50"
        >
          Generate Weekly Workout
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-slate-400 py-8">
            <Icons.Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Hi! I'm your AI fitness assistant.</p>
            <p className="text-sm mt-2">I can help you create workouts, answer questions about functional fitness, and more.</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
              msg.role === 'user' 
                ? 'bg-cyan-primary text-white' 
                : 'bg-ocean-surface text-slate-300'
            }`}>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-ocean-surface rounded-2xl px-4 py-2">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce"></span>
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="p-4 border-t border-white/10">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything..."
            className="flex-1 bg-ocean-surface border border-white/10 rounded-xl px-4 py-2 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
            disabled={loading}
          />
          <button 
            type="submit" 
            disabled={loading || !input.trim()}
            className="btn-cyan px-4 disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </form>
    </div>
  );
};

// ==================== ADMIN DASHBOARD ====================
const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAI, setShowAI] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to load
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, workoutsRes, draftsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/workouts`),
        axios.get(`${API}/ai/workout-drafts`),
        axios.get(`${API}/admin/users`)
      ]);
      setStats(statsRes.data);
      setWorkouts(workoutsRes.data);
      setDrafts(draftsRes.data);
      setUsers(usersRes.data);
    } catch (e) {
      console.error('Error fetching admin data:', e);
    }
  };

  const approveDraft = async (draftId) => {
    try {
      await axios.post(`${API}/ai/workout-drafts/${draftId}/approve`);
      fetchData();
      alert('Workout approved and published!');
    } catch (e) {
      console.error('Error approving draft:', e);
      alert('Failed to approve workout');
    }
  };

  const deleteDraft = async (draftId) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await axios.delete(`${API}/ai/workout-drafts/${draftId}`);
      fetchData();
    } catch (e) {
      console.error('Error deleting draft:', e);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-ocean-dark flex items-center justify-center pt-24">
        <div className="text-cyan-400 animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-ocean-dark pt-24 pb-16 px-6" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="font-syne font-bold text-3xl text-white">Admin Dashboard</h1>
            <p className="text-slate-400">Manage your fitness platform</p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => setShowAI(true)}
              className="btn-secondary flex items-center gap-2"
              data-testid="ai-assistant-btn"
            >
              <Icons.Zap />
              AI Assistant
            </button>
            <button 
              onClick={() => navigate('/admin/workouts/new')}
              className="btn-cyan flex items-center gap-2"
              data-testid="add-workout-btn"
            >
              <Icons.Plus />
              Add Workout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-white/10 overflow-x-auto">
          {['overview', 'drafts', 'workouts', 'users', 'referrals'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`admin-tab-${tab}`}
              className={`pb-4 px-2 font-medium capitalize transition-colors whitespace-nowrap ${
                activeTab === tab 
                  ? 'text-cyan-400 border-b-2 border-cyan-400' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab === 'drafts' && drafts.length > 0 ? `${tab} (${drafts.length})` : tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: 'Total Users', value: stats.total_users, icon: Icons.User },
              { label: 'Active Members', value: stats.active_members, icon: Icons.Shield },
              { label: 'Family Members', value: stats.family_members, icon: Icons.User },
              { label: 'Get After It Workouts', value: stats.get_after_it_workouts, icon: Icons.Flame },
              { label: 'Weekly Workouts', value: stats.total_workouts, icon: Icons.Dumbbell },
              { label: 'Exercises', value: stats.total_exercises, icon: Icons.Library },
              { label: 'Pending Drafts', value: stats.pending_drafts, icon: Icons.Clock },
            ].map((stat, i) => (
              <div key={i} className="glass-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-400 text-sm">{stat.label}</span>
                  <div className="w-10 h-10 rounded-xl bg-cyan-primary/10 flex items-center justify-center text-cyan-400">
                    <stat.icon />
                  </div>
                </div>
                <p className="font-syne font-bold text-3xl text-white">{stat.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Drafts Tab */}
        {activeTab === 'drafts' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-syne font-bold text-xl text-white">AI-Generated Workout Drafts</h2>
              <p className="text-slate-400 text-sm">Drafts auto-publish Sunday 8pm if not approved</p>
            </div>
            {drafts.length === 0 ? (
              <div className="text-center py-12 glass-card">
                <Icons.Clock className="w-12 h-12 mx-auto text-slate-500 mb-4" />
                <p className="text-slate-400">No pending drafts</p>
                <p className="text-slate-500 text-sm mt-2">Use the AI Assistant to generate new workouts</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {drafts.map(draft => (
                  <div key={draft.id} className="glass-card p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            draft.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                            draft.status === 'published' ? 'bg-green-500/20 text-green-400' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>
                            {draft.status.toUpperCase()}
                          </span>
                          {draft.auto_publish_at && draft.status === 'draft' && (
                            <span className="text-xs text-slate-500">
                              Auto-publish: {new Date(draft.auto_publish_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <h3 className="font-syne font-bold text-xl text-white">{draft.title}</h3>
                        <p className="text-slate-400 text-sm mt-1">{draft.description}</p>
                        <p className="text-slate-500 text-xs mt-2">
                          {draft.days?.length || 0} days • Created {new Date(draft.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {draft.status === 'draft' && (
                        <div className="flex gap-2">
                          <button 
                            onClick={() => approveDraft(draft.id)}
                            className="btn-cyan text-sm px-4 py-2"
                          >
                            Approve & Publish
                          </button>
                          <button 
                            onClick={() => deleteDraft(draft.id)}
                            className="text-red-400 hover:text-red-300 p-2"
                          >
                            <Icons.Trash />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Workouts Tab */}
        {activeTab === 'workouts' && (
          <div className="space-y-4">
            {workouts.map(workout => (
              <div key={workout.id} className="glass-card p-6 flex items-center justify-between">
                <div>
                  <h3 className="font-syne font-bold text-xl text-white">{workout.title}</h3>
                  <p className="text-slate-400">Week {workout.week_number} • {workout.days?.length || 0} days</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    workout.is_active 
                      ? 'bg-green-500/10 text-green-400' 
                      : 'bg-slate-500/10 text-slate-400'
                  }`}>
                    {workout.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <button 
                    onClick={() => navigate(`/admin/workouts/${workout.id}`)}
                    className="text-cyan-400 hover:text-cyan-300"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="glass-card overflow-hidden">
              <table className="w-full">
                <thead className="bg-ocean-surface/50">
                  <tr>
                    <th className="text-left p-4 text-slate-400 font-medium">Name</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Email/Username</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Type</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Referral Code</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Referrals</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-t border-white/5">
                      <td className="p-4 text-white">{u.name}</td>
                      <td className="p-4 text-slate-300">{u.email || u.username}</td>
                      <td className="p-4">
                        {u.is_admin ? (
                          <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-xs">Admin</span>
                        ) : u.is_family ? (
                          <span className="bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded text-xs">Family</span>
                        ) : (
                          <span className="bg-slate-500/20 text-slate-400 px-2 py-1 rounded text-xs">Member</span>
                        )}
                      </td>
                      <td className="p-4 text-cyan-400 font-mono">{u.referral_code || '-'}</td>
                      <td className="p-4 text-white">{u.referral_count || 0}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          u.is_member || u.subscription_status === 'active' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-slate-500/20 text-slate-400'
                        }`}>
                          {u.is_member || u.subscription_status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            <div className="glass-card p-6">
              <h2 className="font-syne font-bold text-xl text-white mb-4">Monthly Referral Leaderboard</h2>
              <p className="text-slate-400 text-sm mb-6">Top referrers get a custom t-shirt!</p>
              <div className="space-y-3">
                {users
                  .filter(u => u.referral_count > 0)
                  .sort((a, b) => (b.referral_count || 0) - (a.referral_count || 0))
                  .map((u, i) => (
                    <div key={u.id} className="flex items-center justify-between bg-ocean-surface/50 rounded-xl p-4">
                      <div className="flex items-center gap-4">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          i === 0 ? 'bg-yellow-500 text-black' :
                          i === 1 ? 'bg-slate-400 text-black' :
                          i === 2 ? 'bg-orange-600 text-white' :
                          'bg-ocean-surface text-slate-400'
                        }`}>
                          {i + 1}
                        </span>
                        <div>
                          <p className="text-white font-medium">{u.name}</p>
                          <p className="text-slate-500 text-sm font-mono">{u.referral_code}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-cyan-400 font-bold text-2xl">{u.referral_count}</p>
                        <p className="text-slate-500 text-xs">sign-ups</p>
                      </div>
                    </div>
                  ))}
                {users.filter(u => u.referral_count > 0).length === 0 && (
                  <p className="text-center text-slate-400 py-8">No referrals yet this month</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AI Assistant */}
      {showAI && <AIAssistant onClose={() => setShowAI(false)} />}
    </div>
  );
};

// ==================== ADMIN WORKOUT EDITOR ====================
const AdminWorkoutEditor = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { workoutId } = window.location.pathname.match(/workouts\/(?<workoutId>[^/]+)/)?.groups || {};
  const isNew = workoutId === 'new';
  
  const [workout, setWorkout] = useState({
    week_number: 1,
    title: '',
    description: '',
    is_active: true,
    days: [
      { day: 1, title: '', subtitle: '', duration: '45 min', level: 'Intermediate', focus_areas: [], warmup: [], exercises: [], burnout: [], card_image_url: '' },
      { day: 2, title: '', subtitle: '', duration: '45 min', level: 'Intermediate', focus_areas: [], warmup: [], exercises: [], burnout: [], card_image_url: '' },
      { day: 3, title: '', subtitle: '', duration: '45 min', level: 'Intermediate', focus_areas: [], warmup: [], exercises: [], burnout: [], card_image_url: '' },
      { day: 4, title: '', subtitle: '', duration: '45 min', level: 'Intermediate', focus_areas: [], warmup: [], exercises: [], burnout: [], card_image_url: '' },
      { day: 5, title: '', subtitle: '', duration: '45 min', level: 'Intermediate', focus_areas: [], warmup: [], exercises: [], burnout: [], card_image_url: '' },
    ]
  });
  const [activeDay, setActiveDay] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    if (!isNew && workoutId) {
      fetchWorkout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, workoutId, isNew]);

  const fetchWorkout = async () => {
    try {
      const res = await axios.get(`${API}/workouts/${workoutId}`);
      setWorkout(res.data);
    } catch (e) {
      console.error('Error fetching workout:', e);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/workouts`, workout);
      } else {
        await axios.put(`${API}/workouts/${workoutId}`, workout);
      }
      navigate('/admin');
    } catch (e) {
      console.error('Error saving workout:', e);
      alert('Error saving workout');
    } finally {
      setSaving(false);
    }
  };

  const updateDay = (field, value) => {
    setWorkout(prev => ({
      ...prev,
      days: prev.days.map(d => 
        d.day === activeDay ? { ...d, [field]: value } : d
      )
    }));
  };

  const currentDay = workout.days.find(d => d.day === activeDay) || workout.days[0];
  const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI'];

  const addExercise = () => {
    updateDay('exercises', [...(currentDay.exercises || []), { name: '', sets: '3', reps: '10', notes: '' }]);
  };

  const updateExercise = (index, field, value) => {
    const newExercises = [...(currentDay.exercises || [])];
    newExercises[index] = { ...newExercises[index], [field]: value };
    updateDay('exercises', newExercises);
  };

  const removeExercise = (index) => {
    updateDay('exercises', currentDay.exercises.filter((_, i) => i !== index));
  };

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-ocean-dark pt-24 pb-16 px-6" data-testid="workout-editor">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-syne font-bold text-3xl text-white">
            {isNew ? 'Create Workout' : 'Edit Workout'}
          </h1>
          <div className="flex gap-3">
            <button onClick={() => navigate('/admin')} className="btn-secondary">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="btn-cyan">
              {saving ? 'Saving...' : 'Save Workout'}
            </button>
          </div>
        </div>

        {/* Basic Info */}
        <div className="glass-card p-6 mb-6">
          <h2 className="font-syne font-bold text-xl text-white mb-4">Basic Info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-2">Week Number</label>
              <input
                type="number"
                value={workout.week_number}
                onChange={(e) => setWorkout(prev => ({ ...prev, week_number: parseInt(e.target.value) }))}
                className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-2">Title</label>
              <input
                type="text"
                value={workout.title}
                onChange={(e) => setWorkout(prev => ({ ...prev, title: e.target.value }))}
                className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white"
                placeholder="Week 2 - Flow & Power"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-slate-300 mb-2">Description</label>
              <textarea
                value={workout.description}
                onChange={(e) => setWorkout(prev => ({ ...prev, description: e.target.value }))}
                className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white h-20"
                placeholder="Week description..."
              />
            </div>
          </div>
        </div>

        {/* Day Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {dayNames.map((name, index) => (
            <button
              key={index}
              onClick={() => setActiveDay(index + 1)}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                activeDay === index + 1 
                  ? 'bg-cyan-primary text-ocean-dark' 
                  : 'bg-ocean-surface text-slate-300 hover:bg-ocean-surface/80'
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        {/* Day Content */}
        <div className="glass-card p-6 mb-6">
          <h2 className="font-syne font-bold text-xl text-white mb-4">Day {activeDay} Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm text-slate-300 mb-2">Title</label>
              <input
                type="text"
                value={currentDay.title}
                onChange={(e) => updateDay('title', e.target.value)}
                className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white"
                placeholder="Roots & Reach"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-2">Subtitle</label>
              <input
                type="text"
                value={currentDay.subtitle}
                onChange={(e) => updateDay('subtitle', e.target.value)}
                className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white"
                placeholder="Foundation Power"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-2">Duration</label>
              <input
                type="text"
                value={currentDay.duration}
                onChange={(e) => updateDay('duration', e.target.value)}
                className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white"
                placeholder="45 min"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-2">Level</label>
              <select
                value={currentDay.level}
                onChange={(e) => updateDay('level', e.target.value)}
                className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white"
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="All Levels">All Levels</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-slate-300 mb-2">Card Image URL</label>
              <input
                type="text"
                value={currentDay.card_image_url}
                onChange={(e) => updateDay('card_image_url', e.target.value)}
                className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Warmup */}
          <div className="mb-6">
            <label className="block text-sm text-slate-300 mb-2">Warmup (comma separated)</label>
            <input
              type="text"
              value={currentDay.warmup?.join(', ') || ''}
              onChange={(e) => updateDay('warmup', e.target.value.split(', ').filter(Boolean))}
              className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white"
              placeholder="Dynamic stretching, Hip circles, Leg swings"
            />
          </div>

          {/* Exercises */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <label className="text-sm text-slate-300">Exercises</label>
              <button onClick={addExercise} className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1 text-sm">
                <Icons.Plus /> Add Exercise
              </button>
            </div>
            <div className="space-y-3">
              {currentDay.exercises?.map((ex, i) => (
                <div key={i} className="bg-ocean-surface/50 rounded-xl p-4">
                  <div className="grid grid-cols-12 gap-3 items-center">
                    <input
                      type="text"
                      value={ex.name}
                      onChange={(e) => updateExercise(i, 'name', e.target.value)}
                      className="col-span-5 bg-ocean-surface border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Exercise name"
                    />
                    <input
                      type="text"
                      value={ex.sets}
                      onChange={(e) => updateExercise(i, 'sets', e.target.value)}
                      className="col-span-2 bg-ocean-surface border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Sets"
                    />
                    <input
                      type="text"
                      value={ex.reps}
                      onChange={(e) => updateExercise(i, 'reps', e.target.value)}
                      className="col-span-3 bg-ocean-surface border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Reps"
                    />
                    <button 
                      onClick={() => removeExercise(i)}
                      className="col-span-2 text-red-400 hover:text-red-300 flex justify-center"
                    >
                      <Icons.Trash />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={ex.notes}
                    onChange={(e) => updateExercise(i, 'notes', e.target.value)}
                    className="w-full mt-2 bg-ocean-surface border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="Notes"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Burnout */}
          <div>
            <label className="block text-sm text-slate-300 mb-2">Burnout (comma separated)</label>
            <input
              type="text"
              value={currentDay.burnout?.join(', ') || ''}
              onChange={(e) => updateDay('burnout', e.target.value.split(', ').filter(Boolean))}
              className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white"
              placeholder="Bodyweight squats x 20, Plank hold 60s"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== ABOUT PAGE ====================
const AboutPage = () => (
  <div className="min-h-screen bg-ocean-dark pt-24 pb-16 px-6" data-testid="about-page">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-16">
        <div className="flex justify-center mb-6">
          <div 
            className="rounded-full overflow-hidden logo-hover-effect"
            style={{ 
              width: 120, 
              height: 120,
              border: '3px solid #00B4D8',
              boxShadow: '0 0 30px rgba(0, 180, 216, 0.4)',
            }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_a2502930-3d84-478a-90cd-13560982946b/artifacts/gstc47se_file_1772340471929.jpeg"
              alt="Move Free Live Free"
              className="w-full h-full object-cover"
              style={{ transform: 'scale(1.25)' }}
            />
          </div>
        </div>
        <h1 className="font-syne font-bold text-4xl md:text-5xl text-white mt-8 mb-4">
          About Move Free Live Free
        </h1>
        <p className="text-slate-400 text-lg">
          Movement Vitality & Functionality
        </p>
      </div>

      <div className="prose prose-invert max-w-none">
        <div className="glass-card p-8 mb-8">
          <h2 className="font-syne font-bold text-2xl text-white mb-4">Our Philosophy</h2>
          <p className="text-slate-300 leading-relaxed">
            At Move Free Live Free, we believe that true fitness comes from mastering the fundamental movement patterns that humans have evolved to perform. Our approach is based on the Prime8 Movement Patterns - Push, Pull, Squat, Lunge, Hinge, Rotation, Athletic, Ballistic, and Stability - the building blocks of all human movement.
          </p>
        </div>

        <div className="glass-card p-8 mb-8">
          <h2 className="font-syne font-bold text-2xl text-white mb-4">The Prime8</h2>
          <p className="text-slate-300 leading-relaxed mb-6">
            Every exercise, every sport, every daily activity can be broken down into these eight fundamental patterns. By training these patterns, you build a body that is not only strong but functional, mobile, and resilient.
          </p>
          <ul className="space-y-3">
            {[
              { name: 'Push', desc: 'Upper body pressing power' },
              { name: 'Pull', desc: 'Back and grip strength' },
              { name: 'Squat/Lunge', desc: 'Lower body & lateral movements' },
              { name: 'Hinge', desc: 'Posterior chain power' },
              { name: 'Core Rotation', desc: "Body's center of power & control" },
              { name: 'Athletic & Rhythmatic', desc: 'Controlled repetitive unified body movement' },
              { name: 'Ballistic & Explosive', desc: 'Singular release of force in one direction' },
              { name: 'Stability & Move-ability', desc: 'Strength to withstand load and move with control' },
            ].map((pattern, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-300">
                <span className="text-cyan-400 font-bold">{pattern.name}:</span>
                {pattern.desc}
              </li>
            ))}
          </ul>
        </div>

        <blockquote className="glass-card p-8 border-l-4 border-l-cyan-400">
          <p className="font-syne text-2xl text-white italic">
            "Be water, my friend. Empty your mind, be formless, shapeless — like water."
          </p>
          <cite className="text-cyan-400 mt-4 block">— Bruce Lee</cite>
        </blockquote>
      </div>
    </div>
  </div>
);

// ==================== CONTACT PAGE ====================
const ContactPage = () => (
  <div className="min-h-screen bg-ocean-dark pt-24 pb-16 px-6" data-testid="contact-page">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="font-syne font-bold text-4xl md:text-5xl text-white mb-4">
          Contact Us
        </h1>
        <p className="text-slate-400 text-lg">
          We'd love to hear from you
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="glass-card p-8">
          <h2 className="font-syne font-bold text-2xl text-white mb-6">Get in Touch</h2>
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-cyan-primary/10 flex items-center justify-center text-cyan-400">
                <Icons.Mail />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Email</p>
                <a href="mailto:drjccarr@gmail.com" className="text-white hover:text-cyan-400 transition-colors">
                  drjccarr@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card p-8">
          <h2 className="font-syne font-bold text-2xl text-white mb-6">Send a Message</h2>
          <form className="space-y-4">
            <div>
              <input
                type="text"
                placeholder="Your Name"
                className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <input
                type="email"
                placeholder="Your Email"
                className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <textarea
                placeholder="Your Message"
                rows={4}
                className="w-full bg-ocean-surface border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500"
              />
            </div>
            <button type="submit" className="btn-cyan w-full">
              Send Message
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
);

// ==================== SUCCESS PAGE ====================
const MembershipSuccessPage = () => {
  const { fetchUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      pollStatus(sessionId);
    } else {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const pollStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        await fetchUser();
        setTimeout(() => navigate('/workouts'), 3000);
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
      }
    } catch (e) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-ocean-dark flex items-center justify-center px-6">
      <div className="glass-card p-12 text-center max-w-md">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 border-4 border-cyan-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-syne font-bold text-2xl text-white mb-2">Verifying Payment</h1>
            <p className="text-slate-400">Please wait...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Icons.Check />
            </div>
            <h1 className="font-syne font-bold text-2xl text-white mb-2">Welcome to the Movement!</h1>
            <p className="text-slate-400 mb-4">Your membership is now active.</p>
            <p className="text-cyan-400 text-sm">Redirecting to workouts...</p>
          </>
        )}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <h1 className="font-syne font-bold text-2xl text-white mb-2">
              {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
            </h1>
            <p className="text-slate-400 mb-6">Please try again or contact support.</p>
            <button onClick={() => navigate('/membership')} className="btn-cyan">
              Return to Membership
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== ASSESSMENT PAGE ====================
const AssessmentPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [patterns, setPatterns] = useState([]);
  const [ratings, setRatings] = useState({});
  const [existingAssessment, setExistingAssessment] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('results');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [patternsRes, assessmentRes, recsRes, historyRes] = await Promise.all([
        axios.get(`${API}/patterns`),
        axios.get(`${API}/assessments/me`),
        axios.get(`${API}/assessments/recommendations`),
        axios.get(`${API}/assessments/history`)
      ]);
      setPatterns(patternsRes.data);
      setExistingAssessment(assessmentRes.data);
      setRecommendations(recsRes.data);
      setHistory(historyRes.data);
      
      // Initialize ratings from existing assessment or defaults
      const initialRatings = {};
      patternsRes.data.forEach(p => {
        const existing = assessmentRes.data?.ratings?.find(r => r.pattern_id === p.id);
        initialRatings[p.id] = existing?.rating || 3;
      });
      setRatings(initialRatings);
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const ratingsArray = patterns.map(p => ({
        pattern_id: p.id,
        pattern_name: p.name,
        rating: ratings[p.id] || 3
      }));
      
      await axios.post(`${API}/assessments`, { ratings: ratingsArray });
      await fetchData();
      setShowForm(false);
    } catch (e) {
      console.error('Error submitting assessment:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Needs Work',
      2: 'Developing',
      3: 'Competent',
      4: 'Proficient',
      5: 'Mastered'
    };
    return labels[rating] || 'Unknown';
  };

  const getRatingColor = (rating) => {
    if (rating <= 2) return 'text-red-400';
    if (rating === 3) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getPriorityColor = (priority) => {
    if (priority === 'high') return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (priority === 'medium') return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    return 'bg-green-500/20 text-green-400 border-green-500/30';
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-ocean-dark flex items-center justify-center pt-20">
        <div className="text-cyan-400 animate-pulse">Loading assessment...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ocean-dark pt-24 pb-16 px-6" data-testid="assessment-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-syne font-bold text-4xl md:text-5xl text-white mb-4">
            Movement Assessment
          </h1>
          <p className="text-slate-400 text-lg">
            Rate your proficiency in each Prime8 pattern to get personalized recommendations
          </p>
        </div>

        {/* Assessment Results or New Assessment */}
        {recommendations?.has_assessment && !showForm ? (
          <div className="space-y-8">
            {/* Tab Navigation */}
            <div className="flex justify-center gap-2 mb-8">
              {['results', 'radar', 'progress'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 rounded-xl font-medium capitalize transition-all ${
                    activeTab === tab 
                      ? 'bg-cyan-primary text-ocean-dark' 
                      : 'bg-ocean-surface text-slate-300 hover:bg-ocean-surface/80'
                  }`}
                  data-testid={`tab-${tab}`}
                >
                  {tab === 'results' ? 'Results' : tab === 'radar' ? 'Radar Chart' : 'Progress'}
                </button>
              ))}
            </div>

            {/* Results Tab */}
            {activeTab === 'results' && (
              <>
                {/* Overall Score Card */}
                <div className="glass-card p-8 text-center">
                  <h2 className="font-syne font-bold text-2xl text-white mb-4">Your Movement Score</h2>
                  <div className="relative inline-flex items-center justify-center mb-6">
                    <svg className="w-32 h-32 transform -rotate-90">
                      <circle cx="64" cy="64" r="56" stroke="#1E293B" strokeWidth="12" fill="none" />
                      <circle 
                        cx="64" cy="64" r="56" 
                        stroke="url(#scoreGradient)" 
                        strokeWidth="12" 
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${(recommendations.overall_score / 100) * 352} 352`}
                      />
                      <defs>
                        <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#00B4D8" />
                          <stop offset="100%" stopColor="#2DD4BF" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <span className="absolute font-syne font-bold text-3xl text-white">
                      {Math.round(recommendations.overall_score)}%
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 max-w-md mx-auto text-left">
                    <div>
                      <h3 className="text-green-400 font-semibold mb-2">Strengths</h3>
                      {recommendations.strengths.length > 0 ? (
                        <ul className="space-y-1">
                          {recommendations.strengths.map((s, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-center gap-2">
                              <Icons.Check /> {s}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-slate-500 text-sm">Keep working!</p>
                      )}
                    </div>
                    <div>
                      <h3 className="text-red-400 font-semibold mb-2">Focus Areas</h3>
                      {recommendations.weaknesses.length > 0 ? (
                        <ul className="space-y-1">
                          {recommendations.weaknesses.map((w, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-center gap-2">
                              <Icons.Target /> {w}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-slate-500 text-sm">Well balanced!</p>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-slate-500 text-sm mt-6">
                    Last assessed: {new Date(recommendations.assessment_date).toLocaleDateString()}
                  </p>
                  
                  <button 
                    onClick={() => setShowForm(true)}
                    className="btn-secondary mt-6"
                    data-testid="retake-assessment-btn"
                  >
                    Retake Assessment
                  </button>
                </div>

                {/* Recommendations */}
                <div>
                  <h2 className="font-syne font-bold text-2xl text-white mb-6">Your Personalized Plan</h2>
                  <div className="space-y-4">
                    {recommendations.recommendations.map((rec, i) => (
                      <div 
                        key={i} 
                        className={`glass-card p-6 border-l-4 ${
                          rec.priority === 'high' ? 'border-l-red-500' : 
                          rec.priority === 'medium' ? 'border-l-yellow-500' : 'border-l-green-500'
                        }`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                          <div>
                            <h3 className="font-syne font-bold text-xl text-white">{rec.pattern}</h3>
                            <p className="text-slate-400 text-sm">{rec.focus_message}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(rec.priority)}`}>
                              {rec.priority.toUpperCase()} PRIORITY
                            </span>
                            <span className={`font-bold ${getRatingColor(rec.current_rating)}`}>
                              {rec.current_rating}/5
                            </span>
                          </div>
                        </div>
                        
                        {rec.exercises.length > 0 && (
                          <div>
                            <h4 className="text-slate-300 text-sm font-medium mb-2">Recommended Exercises:</h4>
                            <div className="flex flex-wrap gap-2">
                              {rec.exercises.map((ex, j) => (
                                <span key={j} className="bg-ocean-surface px-3 py-1 rounded-full text-sm text-slate-300">
                                  {ex}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Radar Chart Tab */}
            {activeTab === 'radar' && existingAssessment && (
              <div className="glass-card p-8">
                <h2 className="font-syne font-bold text-2xl text-white mb-6 text-center">Prime8 Radar</h2>
                <div className="flex justify-center">
                  <svg viewBox="0 0 400 400" className="w-full max-w-md">
                    {/* Background circles */}
                    {[1, 2, 3, 4, 5].map(level => (
                      <circle
                        key={level}
                        cx="200"
                        cy="200"
                        r={level * 35}
                        fill="none"
                        stroke="#1E293B"
                        strokeWidth="1"
                      />
                    ))}
                    
                    {/* Axis lines */}
                    {existingAssessment.ratings.map((_, i) => {
                      const angle = (i * 360 / existingAssessment.ratings.length - 90) * Math.PI / 180;
                      const x2 = 200 + 175 * Math.cos(angle);
                      const y2 = 200 + 175 * Math.sin(angle);
                      return (
                        <line
                          key={i}
                          x1="200"
                          y1="200"
                          x2={x2}
                          y2={y2}
                          stroke="#1E293B"
                          strokeWidth="1"
                        />
                      );
                    })}
                    
                    {/* Data polygon */}
                    <polygon
                      points={existingAssessment.ratings.map((r, i) => {
                        const angle = (i * 360 / existingAssessment.ratings.length - 90) * Math.PI / 180;
                        const radius = (r.rating / 5) * 175;
                        const x = 200 + radius * Math.cos(angle);
                        const y = 200 + radius * Math.sin(angle);
                        return `${x},${y}`;
                      }).join(' ')}
                      fill="rgba(0, 180, 216, 0.3)"
                      stroke="#00B4D8"
                      strokeWidth="2"
                    />
                    
                    {/* Data points */}
                    {existingAssessment.ratings.map((r, i) => {
                      const angle = (i * 360 / existingAssessment.ratings.length - 90) * Math.PI / 180;
                      const radius = (r.rating / 5) * 175;
                      const x = 200 + radius * Math.cos(angle);
                      const y = 200 + radius * Math.sin(angle);
                      return (
                        <circle
                          key={i}
                          cx={x}
                          cy={y}
                          r="6"
                          fill="#00B4D8"
                          stroke="#020817"
                          strokeWidth="2"
                        />
                      );
                    })}
                    
                    {/* Labels */}
                    {existingAssessment.ratings.map((r, i) => {
                      const angle = (i * 360 / existingAssessment.ratings.length - 90) * Math.PI / 180;
                      const x = 200 + 195 * Math.cos(angle);
                      const y = 200 + 195 * Math.sin(angle);
                      // Shorten long pattern names
                      const shortName = r.pattern_name.split(' ')[0];
                      return (
                        <text
                          key={i}
                          x={x}
                          y={y}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#94A3B8"
                          fontSize="11"
                          fontWeight="500"
                        >
                          {shortName}
                        </text>
                      );
                    })}
                  </svg>
                </div>
                
                {/* Legend */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                  {existingAssessment.ratings.map((r, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${
                        r.rating >= 4 ? 'bg-green-400' : r.rating >= 3 ? 'bg-yellow-400' : 'bg-red-400'
                      }`} />
                      <span className="text-slate-300 text-sm truncate">{r.pattern_name}</span>
                      <span className="text-slate-500 text-sm">({r.rating})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Progress Tab */}
            {activeTab === 'progress' && (
              <div className="space-y-8">
                {/* Score History Chart */}
                <div className="glass-card p-8">
                  <h2 className="font-syne font-bold text-2xl text-white mb-6">Score History</h2>
                  {history.length > 1 ? (
                    <div className="relative h-64">
                      {/* Y-axis labels */}
                      <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between text-slate-500 text-sm pr-2">
                        <span>100%</span>
                        <span>75%</span>
                        <span>50%</span>
                        <span>25%</span>
                        <span>0%</span>
                      </div>
                      
                      {/* Chart area */}
                      <div className="ml-12 h-full relative">
                        {/* Grid lines */}
                        {[0, 25, 50, 75, 100].map(level => (
                          <div
                            key={level}
                            className="absolute w-full border-t border-white/5"
                            style={{ bottom: `${level}%` }}
                          />
                        ))}
                        
                        {/* Line chart */}
                        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                          <defs>
                            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#00B4D8" />
                              <stop offset="100%" stopColor="#2DD4BF" />
                            </linearGradient>
                            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgba(0, 180, 216, 0.3)" />
                              <stop offset="100%" stopColor="rgba(0, 180, 216, 0)" />
                            </linearGradient>
                          </defs>
                          
                          {/* Area fill */}
                          <path
                            d={`M 0 ${100 - history[history.length - 1]?.overall_score || 0}% ` + 
                               history.slice().reverse().map((h, i) => 
                                 `L ${(i / (history.length - 1)) * 100}% ${100 - h.overall_score}%`
                               ).join(' ') + 
                               ` L 100% 100% L 0 100% Z`}
                            fill="url(#areaGradient)"
                          />
                          
                          {/* Line */}
                          <polyline
                            points={history.slice().reverse().map((h, i) => 
                              `${(i / (history.length - 1)) * 100}%,${100 - h.overall_score}%`
                            ).join(' ')}
                            fill="none"
                            stroke="url(#lineGradient)"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          
                          {/* Data points */}
                          {history.slice().reverse().map((h, i) => (
                            <circle
                              key={i}
                              cx={`${(i / (history.length - 1)) * 100}%`}
                              cy={`${100 - h.overall_score}%`}
                              r="6"
                              fill="#00B4D8"
                              stroke="#020817"
                              strokeWidth="2"
                            />
                          ))}
                        </svg>
                        
                        {/* X-axis labels */}
                        <div className="absolute bottom-0 left-0 right-0 flex justify-between transform translate-y-6">
                          {history.slice().reverse().map((h, i) => (
                            <span key={i} className="text-slate-500 text-xs">
                              {new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-slate-400 mb-4">Complete more assessments to see your progress over time!</p>
                      <button 
                        onClick={() => setShowForm(true)}
                        className="btn-secondary"
                      >
                        Take Another Assessment
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Pattern Progress */}
                {history.length > 1 && (
                  <div className="glass-card p-8">
                    <h2 className="font-syne font-bold text-2xl text-white mb-6">Pattern Progress</h2>
                    <div className="space-y-4">
                      {patterns.map(pattern => {
                        const currentRating = existingAssessment?.ratings?.find(r => r.pattern_id === pattern.id)?.rating || 0;
                        const previousAssessment = history[1];
                        const previousRating = previousAssessment?.ratings?.find(r => r.pattern_id === pattern.id)?.rating || 0;
                        const change = currentRating - previousRating;
                        
                        return (
                          <div key={pattern.id} className="flex items-center gap-4">
                            <div className="w-32 text-slate-300 text-sm truncate">{pattern.name.split(' ')[0]}</div>
                            <div className="flex-1 h-4 bg-ocean-surface rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-cyan-primary to-teal-accent rounded-full transition-all duration-500"
                                style={{ width: `${(currentRating / 5) * 100}%` }}
                              />
                            </div>
                            <div className="w-12 text-right">
                              <span className="text-white font-bold">{currentRating}</span>
                              <span className="text-slate-500">/5</span>
                            </div>
                            <div className={`w-16 text-right text-sm font-medium ${
                              change > 0 ? 'text-green-400' : change < 0 ? 'text-red-400' : 'text-slate-500'
                            }`}>
                              {change > 0 ? `+${change}` : change === 0 ? '—' : change}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-slate-500 text-sm mt-6 text-center">
                      Compared to your previous assessment
                    </p>
                  </div>
                )}
                
                {/* Assessment History List */}
                <div className="glass-card p-8">
                  <h2 className="font-syne font-bold text-2xl text-white mb-6">Assessment History</h2>
                  <div className="space-y-3">
                    {history.map((h, i) => (
                      <div key={i} className="flex items-center justify-between bg-ocean-surface/50 rounded-xl p-4">
                        <div>
                          <p className="text-white font-medium">
                            {new Date(h.created_at).toLocaleDateString('en-US', { 
                              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                            })}
                          </p>
                          <p className="text-slate-400 text-sm">
                            {h.strengths?.length || 0} strengths • {h.weaknesses?.length || 0} focus areas
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-syne font-bold text-cyan-400">
                            {Math.round(h.overall_score)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Assessment Form */
          <div className="glass-card p-8">
            <h2 className="font-syne font-bold text-2xl text-white mb-2">Rate Your Movement Patterns</h2>
            <p className="text-slate-400 mb-8">
              Be honest with yourself - this helps us create the best plan for you.
            </p>
            
            <div className="space-y-8">
              {patterns.map((pattern, index) => (
                <div key={pattern.id} className="border-b border-white/5 pb-6 last:border-0">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-syne font-bold text-lg text-white">{pattern.name}</h3>
                      <p className="text-slate-400 text-sm">{pattern.description}</p>
                    </div>
                    <div className="text-right">
                      <span className={`font-bold text-lg ${getRatingColor(ratings[pattern.id] || 3)}`}>
                        {getRatingLabel(ratings[pattern.id] || 3)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Rating Slider */}
                  <div className="flex items-center gap-4">
                    <span className="text-slate-500 text-sm w-20">Needs Work</span>
                    <div className="flex-1 flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map(num => (
                        <button
                          key={num}
                          onClick={() => setRatings(prev => ({ ...prev, [pattern.id]: num }))}
                          className={`flex-1 h-12 rounded-xl font-bold transition-all ${
                            ratings[pattern.id] === num
                              ? 'bg-cyan-primary text-ocean-dark scale-110'
                              : ratings[pattern.id] > num
                              ? 'bg-cyan-primary/30 text-cyan-400'
                              : 'bg-ocean-surface text-slate-400 hover:bg-ocean-surface/80'
                          }`}
                          data-testid={`rating-${pattern.id}-${num}`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                    <span className="text-slate-500 text-sm w-20 text-right">Mastered</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex gap-4 mt-8">
              {existingAssessment && (
                <button 
                  onClick={() => setShowForm(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              )}
              <button 
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-cyan flex-1 disabled:opacity-50"
                data-testid="submit-assessment-btn"
              >
                {submitting ? 'Saving...' : 'Get My Recommendations'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== CHALLENGES PAGE ====================
const ChallengesPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [checkInModal, setCheckInModal] = useState(null);
  const [checkInRating, setCheckInRating] = useState(3);
  const [earnedBadge, setEarnedBadge] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [activeRes, completedRes, suggestRes, statsRes, badgesRes] = await Promise.all([
        axios.get(`${API}/challenges/active`),
        axios.get(`${API}/challenges/completed`),
        axios.get(`${API}/challenges/suggested`),
        axios.get(`${API}/challenges/stats`),
        axios.get(`${API}/challenges/badges`)
      ]);
      setActiveChallenges(activeRes.data);
      setCompletedChallenges(completedRes.data);
      setSuggestions(suggestRes.data.suggestions || []);
      setStats(statsRes.data);
      setBadges(badgesRes.data.badges || []);
    } catch (e) {
      console.error('Error fetching challenges:', e);
    } finally {
      setLoading(false);
    }
  };

  const createChallenge = async (suggestion) => {
    try {
      await axios.post(`${API}/challenges`, {
        pattern_id: suggestion.pattern_id,
        pattern_name: suggestion.pattern_name,
        starting_rating: suggestion.current_rating,
        target_rating: suggestion.suggested_target,
        duration_weeks: suggestion.suggested_duration
      });
      setShowCreateModal(false);
      setSelectedSuggestion(null);
      await fetchData();
    } catch (e) {
      console.error('Error creating challenge:', e);
    }
  };

  const checkIn = async () => {
    if (!checkInModal) return;
    try {
      const res = await axios.post(`${API}/challenges/${checkInModal.id}/checkin`, {
        rating: checkInRating
      });
      
      if (res.data.badge_earned) {
        setEarnedBadge(res.data.badge_earned);
      }
      
      setCheckInModal(null);
      setCheckInRating(3);
      await fetchData();
    } catch (e) {
      console.error('Error checking in:', e);
    }
  };

  const abandonChallenge = async (challengeId) => {
    if (!window.confirm('Are you sure you want to abandon this challenge?')) return;
    try {
      await axios.delete(`${API}/challenges/${challengeId}`);
      await fetchData();
    } catch (e) {
      console.error('Error abandoning challenge:', e);
    }
  };

  const badgeIcons = {
    Star: Icons.Target,
    Crown: Icons.Shield,
    Zap: Icons.Zap,
    Shield: Icons.Shield,
    Trophy: Icons.Target,
    Flame: Icons.Flame,
    Medal: Icons.Check,
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-ocean-dark flex items-center justify-center pt-20">
        <div className="text-cyan-400 animate-pulse">Loading challenges...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ocean-dark pt-24 pb-16 px-6" data-testid="challenges-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-syne font-bold text-4xl md:text-5xl text-white mb-4">
            Prime8 <span className="gradient-text-horizontal">Challenges</span>
          </h1>
          <p className="text-slate-400 text-lg">
            Set goals for your weakest patterns and track your improvement
          </p>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              { label: 'Active', value: stats.active_challenges, color: 'text-cyan-400' },
              { label: 'Completed', value: stats.completed_challenges, color: 'text-green-400' },
              { label: 'Success Rate', value: `${stats.success_rate}%`, color: 'text-yellow-400' },
              { label: 'Badges', value: badges.length, color: 'text-purple-400' },
            ].map((stat, i) => (
              <div key={i} className="glass-card p-4 text-center">
                <p className={`font-syne font-bold text-2xl ${stat.color}`}>{stat.value}</p>
                <p className="text-slate-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Badges Section */}
        {badges.length > 0 && (
          <div className="glass-card p-6 mb-8">
            <h2 className="font-syne font-bold text-xl text-white mb-4">Your Badges</h2>
            <div className="flex flex-wrap gap-4">
              {badges.map((badge, i) => {
                const IconComponent = badgeIcons[badge.icon] || Icons.Target;
                return (
                  <div key={i} className="flex items-center gap-3 bg-ocean-surface/50 rounded-xl p-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-ocean-dark">
                      <IconComponent />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{badge.name}</p>
                      <p className="text-slate-500 text-xs">{badge.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Active Challenges */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-syne font-bold text-2xl text-white">Active Challenges</h2>
            {suggestions.length > 0 && (
              <button 
                onClick={() => setShowCreateModal(true)}
                className="btn-cyan flex items-center gap-2"
                data-testid="new-challenge-btn"
              >
                <Icons.Plus /> New Challenge
              </button>
            )}
          </div>

          {activeChallenges.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {activeChallenges.map(challenge => (
                <div key={challenge.id} className="glass-card p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-syne font-bold text-xl text-white">{challenge.pattern_name}</h3>
                      <p className="text-slate-400 text-sm">
                        {challenge.weeks_remaining} week{challenge.weeks_remaining !== 1 ? 's' : ''} remaining
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-white">{challenge.current_rating}</span>
                        <Icons.ChevronRight />
                        <span className="text-2xl font-bold text-cyan-400">{challenge.target_rating}</span>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">Progress</span>
                      <span className="text-cyan-400">{Math.round(challenge.progress_percent)}%</span>
                    </div>
                    <div className="h-3 bg-ocean-surface rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-cyan-primary to-teal-accent rounded-full transition-all duration-500"
                        style={{ width: `${challenge.progress_percent}%` }}
                      />
                    </div>
                  </div>

                  {/* Check-ins */}
                  <div className="flex items-center gap-2 mb-4">
                    {Array.from({ length: challenge.duration_weeks }).map((_, i) => {
                      const checkIn = challenge.check_ins?.[i];
                      return (
                        <div 
                          key={i}
                          className={`flex-1 h-2 rounded-full ${
                            checkIn ? 'bg-cyan-primary' : 'bg-ocean-surface'
                          }`}
                          title={checkIn ? `Week ${i + 1}: ${checkIn.rating}/5` : `Week ${i + 1}: Not checked in`}
                        />
                      );
                    })}
                  </div>

                  <div className="flex gap-2">
                    <button 
                      onClick={() => { setCheckInModal(challenge); setCheckInRating(challenge.current_rating); }}
                      className="btn-cyan flex-1 py-2 text-sm"
                      data-testid={`checkin-${challenge.id}`}
                    >
                      Weekly Check-in
                    </button>
                    <button 
                      onClick={() => abandonChallenge(challenge.id)}
                      className="btn-secondary py-2 px-4 text-sm text-red-400 border-red-500/30 hover:bg-red-500/10"
                    >
                      <Icons.X />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="glass-card p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-cyan-primary/10 flex items-center justify-center mx-auto mb-4">
                <Icons.Target />
              </div>
              <h3 className="font-syne font-bold text-xl text-white mb-2">No Active Challenges</h3>
              <p className="text-slate-400 mb-6">Start a challenge to improve your weakest patterns!</p>
              {suggestions.length > 0 ? (
                <button 
                  onClick={() => setShowCreateModal(true)}
                  className="btn-cyan"
                >
                  Start Your First Challenge
                </button>
              ) : (
                <button 
                  onClick={() => navigate('/assessment')}
                  className="btn-cyan"
                >
                  Take Assessment First
                </button>
              )}
            </div>
          )}
        </div>

        {/* Completed Challenges */}
        {completedChallenges.length > 0 && (
          <div>
            <h2 className="font-syne font-bold text-2xl text-white mb-6">Completed Challenges</h2>
            <div className="space-y-3">
              {completedChallenges.map(challenge => (
                <div 
                  key={challenge.id} 
                  className={`glass-card p-4 flex items-center justify-between ${
                    challenge.status === 'completed' ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      challenge.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {challenge.status === 'completed' ? <Icons.Check /> : <Icons.X />}
                    </div>
                    <div>
                      <h3 className="text-white font-medium">{challenge.pattern_name}</h3>
                      <p className="text-slate-400 text-sm">
                        {challenge.starting_rating} → {challenge.current_rating} 
                        {challenge.status === 'completed' && challenge.current_rating >= challenge.target_rating 
                          ? ' (Goal reached!)' 
                          : ` (Target: ${challenge.target_rating})`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {challenge.badge_earned && (
                      <span className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm">
                        Badge Earned
                      </span>
                    )}
                    <span className="text-slate-500 text-sm">
                      {new Date(challenge.completed_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create Challenge Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-syne font-bold text-2xl text-white">Start a Challenge</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                  <Icons.X />
                </button>
              </div>

              {suggestions.length > 0 ? (
                <div className="space-y-4">
                  <p className="text-slate-400 mb-4">Based on your assessment, we recommend these challenges:</p>
                  {suggestions.map((s, i) => (
                    <div 
                      key={i}
                      onClick={() => setSelectedSuggestion(s)}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        selectedSuggestion?.pattern_id === s.pattern_id 
                          ? 'bg-cyan-primary/20 border-2 border-cyan-primary' 
                          : 'bg-ocean-surface hover:bg-ocean-surface/80 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-syne font-bold text-white">{s.pattern_name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          s.priority === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {s.priority.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-400">
                          Current: <span className="text-white">{s.current_rating}/5</span>
                        </span>
                        <span className="text-slate-400">
                          Target: <span className="text-cyan-400">{s.suggested_target}/5</span>
                        </span>
                        <span className="text-slate-400">
                          Duration: <span className="text-white">{s.suggested_duration} week{s.suggested_duration > 1 ? 's' : ''}</span>
                        </span>
                      </div>
                    </div>
                  ))}

                  <button 
                    onClick={() => selectedSuggestion && createChallenge(selectedSuggestion)}
                    disabled={!selectedSuggestion}
                    className="btn-cyan w-full mt-6 disabled:opacity-50"
                    data-testid="confirm-challenge-btn"
                  >
                    Start Challenge
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-400 mb-4">Complete an assessment to get challenge suggestions!</p>
                  <button 
                    onClick={() => { setShowCreateModal(false); navigate('/assessment'); }}
                    className="btn-cyan"
                  >
                    Take Assessment
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Check-in Modal */}
        {checkInModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card p-8 max-w-md w-full">
              <h2 className="font-syne font-bold text-2xl text-white mb-2">Weekly Check-in</h2>
              <p className="text-slate-400 mb-6">{checkInModal.pattern_name}</p>

              <div className="mb-6">
                <label className="block text-sm text-slate-300 mb-3">Rate your current level:</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(num => (
                    <button
                      key={num}
                      onClick={() => setCheckInRating(num)}
                      className={`flex-1 h-14 rounded-xl font-bold text-lg transition-all ${
                        checkInRating === num
                          ? 'bg-cyan-primary text-ocean-dark scale-110'
                          : checkInRating > num
                          ? 'bg-cyan-primary/30 text-cyan-400'
                          : 'bg-ocean-surface text-slate-400 hover:bg-ocean-surface/80'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between text-sm mb-6 p-3 bg-ocean-surface/50 rounded-xl">
                <span className="text-slate-400">Target Rating:</span>
                <span className="text-cyan-400 font-bold">{checkInModal.target_rating}/5</span>
              </div>

              {checkInRating >= checkInModal.target_rating && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 mb-6 text-center">
                  <p className="text-green-400 font-medium">You've reached your goal!</p>
                  <p className="text-slate-400 text-sm">This check-in will complete your challenge.</p>
                </div>
              )}

              <div className="flex gap-3">
                <button 
                  onClick={() => setCheckInModal(null)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button 
                  onClick={checkIn}
                  className="btn-cyan flex-1"
                  data-testid="submit-checkin-btn"
                >
                  Submit Check-in
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Badge Earned Modal */}
        {earnedBadge && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card p-8 max-w-md w-full text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto mb-6 animate-bounce">
                {(() => {
                  const IconComponent = badgeIcons[earnedBadge.icon] || Icons.Target;
                  return <IconComponent />;
                })()}
              </div>
              <h2 className="font-syne font-bold text-3xl text-white mb-2">Badge Earned!</h2>
              <p className="text-cyan-400 text-xl mb-2">{earnedBadge.name}</p>
              <p className="text-slate-400 mb-6">{earnedBadge.description}</p>
              <button 
                onClick={() => setEarnedBadge(null)}
                className="btn-cyan"
              >
                Awesome!
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN APP ====================
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-ocean-dark">
          <Navbar />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/workouts" element={<WorkoutsPage />} />
            <Route path="/get-after-it" element={<GetAfterItPage />} />
            <Route path="/exercises" element={<ExercisesPage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/assessment" element={<AssessmentPage />} />
            <Route path="/challenges" element={<ChallengesPage />} />
            <Route path="/membership" element={<MembershipPage />} />
            <Route path="/membership/success" element={<MembershipSuccessPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/workouts/new" element={<AdminWorkoutEditor />} />
            <Route path="/admin/workouts/:workoutId" element={<AdminWorkoutEditor />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Footer />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
